/**
 * Playback memory — persists per-match-slot resume position and a
 * global playback speed across browser sessions.
 *
 * Storage keys: `tktic_playback_positions` (JSON map) +
 * `tktic_playback_speed` (single number). M11 picks the
 * `tktic_*` namespace deliberately — the legacy app used
 * `replay_*` keys, and the rewrite's first surface to render on the
 * production origin without auth (the public game view) should not
 * leave a "Replay" brand string in the viewer's localStorage. A
 * one-shot migration copies any existing `replay_*` values into the
 * new keys on first read and then never touches the legacy keys
 * again — viewers with old state get a seamless upgrade, and the
 * production origin stops accumulating new `replay_*` keys.
 *
 * Position-memory semantics (ported from legacy `js/player.js`):
 *   - Save every 3 s during playback (caller-driven via an
 *     interval in `<GameShell>`).
 *   - Resume on mount when stored time is >5 s into the match AND
 *     <30 s from the end (don't jump someone past the credits;
 *     don't dump someone into a 2-second cold-start).
 *   - Storage shape: `{ "<matchId>/<slot>": <seconds> }`.
 *
 * Speed-memory semantics: single number, applied to every video
 * the viewer plays. Saved when the viewer changes playback speed
 * via the keyboard shortcut.
 *
 * All localStorage access goes through `try/catch` — private mode
 * + quota errors fall through to no-op, never throw out to the
 * React render.
 */

const POSITIONS_KEY = "tktic_playback_positions";
const SPEED_KEY = "tktic_playback_speed";
const LEGACY_POSITIONS_KEY = "replay_playback_positions";
const LEGACY_SPEED_KEY = "replay_playback_speed";

const RESUME_FLOOR_SECONDS = 5;
const RESUME_END_BUFFER_SECONDS = 30;

type PositionsMap = Record<string, number>;

/** Module-private flag so the legacy → tktic key migration runs
 * once per browser tab. Set on first read of either key. */
let migrationRan = false;

function migrateLegacyKeysOnce(): void {
  if (migrationRan) return;
  migrationRan = true;
  try {
    const newPositions = localStorage.getItem(POSITIONS_KEY);
    if (newPositions === null) {
      const legacy = localStorage.getItem(LEGACY_POSITIONS_KEY);
      if (legacy !== null) {
        // Best-effort: only copy if the legacy value parses as JSON.
        try {
          JSON.parse(legacy);
          localStorage.setItem(POSITIONS_KEY, legacy);
        } catch {
          // Malformed legacy value — drop it on the floor.
        }
      }
    }
    const newSpeed = localStorage.getItem(SPEED_KEY);
    if (newSpeed === null) {
      const legacy = localStorage.getItem(LEGACY_SPEED_KEY);
      if (legacy !== null) {
        const n = Number(legacy);
        if (Number.isFinite(n) && n > 0) {
          localStorage.setItem(SPEED_KEY, String(n));
        }
      }
    }
  } catch {
    // private mode / quota — fall through; the rest of the module
    // also wraps localStorage calls in try/catch.
  }
}

function readPositions(): PositionsMap {
  migrateLegacyKeysOnce();
  try {
    const raw = localStorage.getItem(POSITIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {};
    // Coerce values to finite positive numbers; drop anything else.
    const out: PositionsMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = typeof v === "number" ? v : Number(v);
      if (Number.isFinite(n) && n > 0) out[k] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function writePositions(map: PositionsMap): void {
  try {
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

/** Save the current playback time for this `match/slot`. Caller
 * is expected to throttle to ~every 3 s of playback. Storing a
 * value at or beyond `(duration - END_BUFFER)` is a no-op — the
 * resume logic would reject it anyway. */
export function savePlaybackPosition(
  matchId: string,
  slot: string,
  currentTime: number,
  duration: number,
): void {
  if (!matchId || !slot) return;
  if (!Number.isFinite(currentTime) || currentTime <= RESUME_FLOOR_SECONDS)
    return;
  if (
    Number.isFinite(duration) &&
    duration > 0 &&
    currentTime >= duration - RESUME_END_BUFFER_SECONDS
  )
    return;
  const map = readPositions();
  map[`${matchId}/${slot}`] = currentTime;
  writePositions(map);
}

/** Returns the resume position for this `match/slot`, or `null`
 * if no stored value passes the guard rails (≥5 s in AND <30 s
 * from end). Caller seeks the video to the returned value on
 * `loadedmetadata`. */
export function getResumePosition(
  matchId: string,
  slot: string,
  duration: number,
): number | null {
  if (!matchId || !slot) return null;
  const map = readPositions();
  const stored = map[`${matchId}/${slot}`];
  if (!Number.isFinite(stored) || stored <= RESUME_FLOOR_SECONDS) return null;
  if (
    Number.isFinite(duration) &&
    duration > 0 &&
    stored >= duration - RESUME_END_BUFFER_SECONDS
  )
    return null;
  return stored;
}

/** Clear any stored position for this `match/slot`. Useful on
 * "Restart" actions; not exposed in the M11 UI today. */
export function clearPlaybackPosition(matchId: string, slot: string): void {
  if (!matchId || !slot) return;
  const map = readPositions();
  const key = `${matchId}/${slot}`;
  if (!(key in map)) return;
  delete map[key];
  writePositions(map);
}

/** Read the viewer's preferred playback speed (default 1.0). */
export function getPlaybackSpeed(): number {
  migrateLegacyKeysOnce();
  try {
    const raw = localStorage.getItem(SPEED_KEY);
    if (!raw) return 1.0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 1.0;
    // Sanity clamp — the legacy keyboard shortcut steps in 0.25
    // increments; bound to a reasonable range so a corrupted
    // value doesn't render a stuck-fast or stuck-slow player.
    if (n < 0.25) return 0.25;
    if (n > 4.0) return 4.0;
    return n;
  } catch {
    return 1.0;
  }
}

/** Persist the viewer's preferred playback speed. */
export function savePlaybackSpeed(speed: number): void {
  if (!Number.isFinite(speed) || speed <= 0) return;
  try {
    localStorage.setItem(SPEED_KEY, String(speed));
  } catch {
    // ignore
  }
}
